// California State Polytechnic University, Pomona: public college page (#2596), official facts only.
//
// Every figure below was read on 2026-09-30 from the source it names:
//  - Cal Poly Pomona's Common Data Set 2025-2026 (cpp.edu/data, file
//    cds2025.pdf; its cover and headers say "Common Data Set 2025-2026"):
//    C1 applicants/admits/enrollees for Fall 2025 with the residency table,
//    C8A (no use of SAT or ACT), C21/C22 (left blank), G1/G5 2026-2027
//    costs. Quirk: section G checks the box saying final 2026-2027 costs
//    were not yet available (expected 06/2026) yet lists figures; those
//    figures are shown as published, cents included, and the note says so.
//    The cost rows are ordered so the exact sum holds in floating point.
//  - cpp.edu admissions pages: freshman requirements (SAT/ACT statement),
//    freshman impacted majors (the fall 2027 list).
//  - calstate.edu Application Dates & Deadlines (the systemwide Cal State
//    Apply priority filing period for fall 2027).
//  - College Scorecard school page, id 110529 (net price by family income;
//    the API was rate-limited, so the figures come from the official page).
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Cal Poly Pomona Common Data Set 2025-2026',
        url: 'https://www.cpp.edu/data/documents/common-data-sets/cds2025.pdf',
        read: '2026-09-30',
    },
    requirements: {
        label: 'Cal Poly Pomona Admissions, Freshmen Requirements',
        url: 'https://www.cpp.edu/admissions/freshmen/requirements.shtml',
        read: '2026-09-30',
    },
    impacted: {
        label: 'Cal Poly Pomona Admissions, Freshman Impacted Majors',
        url: 'https://www.cpp.edu/admissions/freshmen/impacted-majors.shtml',
        read: '2026-09-30',
    },
    csuDates: {
        label: 'California State University, Application Dates and Deadlines',
        url: 'https://www.calstate.edu/apply/Pages/application-dates-deadlines.aspx',
        read: '2026-09-30',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?110529-California-State-Polytechnic-University-Pomona',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'california-state-polytechnic-university-pomona',
    kbId: 'california_state_polytechnic_university_pomona',
    name: 'California State Polytechnic University, Pomona',
    shortName: 'Cal Poly Pomona',
    aliases: ['Cal Poly', 'CPP'],
    location: 'Pomona, CA',
    control: 'Public',
    lastChecked: '2026-09-30',
    sources: SOURCES,
    // From the KB's sourced branding (CPP design toolkit); decoration only.
    branding: {
        primary: '#005030',
        secondary: '#FFB81C',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/california_state_polytechnic_university_pomona_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 48258,
        admitted: 35688,
        enrolled: 4004,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'California residents', applied: 45487, admitted: 33895 },
            { group: 'Out-of-state', applied: 2378, admitted: 1600 },
            { group: 'International', applied: 393, admitted: 193 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Cal Poly Pomona\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision and early action',
        note: 'The Common Data Set leaves its early decision and early action questions blank, so it gives no early round counts. Cal Poly Pomona\'s admissions pages list only the one Cal State Apply filing period.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Cal Poly Pomona is not impacted at the campus level for first-year applicants, but these majors are. Admission to them is competitive and can take higher academic standards or major-specific criteria beyond the CSU minimum:',
        rows: [
            { school: 'College of Agriculture', requirement: 'Animal Health Science; Animal Science (General); Animal Science (Pre-Vet).' },
            { school: 'College of Business Administration', requirement: 'Accounting; Finance, Real Estate, and Law (impacted beginning with the fall 2027 term).' },
            { school: 'College of Engineering', requirement: 'Aerospace, Civil, Computer, Electrical and Mechanical Engineering.' },
            { school: 'College of Environmental Design', requirement: 'Architecture.' },
            { school: 'College of Letters, Arts and Social Sciences', requirement: 'Psychology.' },
            { school: 'College of Science', requirement: 'Biology; Biotechnology; Environmental Biology; Chemistry; Computer Science; Physics.' },
        ],
        note: 'Applicants to impacted majors from Cal Poly Pomona\'s local area get a bump in their eligibility index. Kinesiology (General) is no longer impacted beginning with the fall 2027 application cycle.',
        source: 'impacted',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Application window', value: 'Oct 1 to Nov 30' },
        rows: [
            { plan: 'Cal State Apply priority filing', deadline: 'November 30', decision: null, binding: false },
        ],
        note: 'You apply through Cal State Apply. You can start working on the application on August 1, 2026; submission opens October 1, 2026, and the priority filing period ends November 30, 2026.',
        source: 'csuDates',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-blind',
        blind: true,
        line: 'The CSU no longer uses SAT or ACT scores in undergraduate admission decisions. You may still send scores, but they are not used for admission. If you are admitted, they may be used to place you in first-year English and math.',
        source: 'requirements',
        cdsLine: 'The Common Data Set agrees: it answers no to using SAT or ACT scores in admission decisions.',
        cdsWhere: 'section C8A',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-09-30',
        changes: [],
        source: 'csuDates',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [6838, 20968] },
            { item: 'Food and housing (on campus)', amounts: [16909, 16909] },
            { item: 'Required fees', amounts: [1727.94, 1727.94] },
            { item: 'Books and supplies', amounts: [1112, 1112] },
            { item: 'Transportation', amounts: [1896, 1896] },
            { item: 'Other expenses', amounts: [2778, 2778] },
        ],
        totals: [31260.94, 45390.94],
        note: 'The Common Data Set\'s figures for a student living on campus. When it was filed, the Common Data Set marked its 2026-2027 costs as not yet final.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 9404,
            byIncome: [
                { income: '$0 to $30,000', amount: 6245 },
                { income: '$30,001 to $48,000', amount: 6570 },
                { income: '$48,001 to $75,000', amount: 7638 },
                { income: '$75,001 to $110,000', amount: 11214 },
                { income: 'Over $110,000', amount: 19582 },
            ],
            note: 'What first-year, full-time in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Cal Poly Pomona\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
